import React from 'react';
import Tooltip from 'admin/components/common/Tooltip';
import { MediaQueryIcon } from 'admin/components/common/MediaQueryIcon';
import MediaQueryTooltip from 'admin/components/common/MediaQueryIcon/MediaQueryTooltip';
import { getDesignPresetNameParts } from 'common/design-tokens/preset-paths';
import t from 'utils/translate';
import { getDesignPresetSummary } from './design-preset-summary';
import { DESIGN_PRESET_PREVIEW_MEDIA_QUERY } from './design-preset-display';
import css from './DesignPresetSummary.scss';

type SummaryValue = ReturnType<typeof getDesignPresetSummary>['properties'][number]['values'][number];

function valueText(value: SummaryValue | undefined) {
	return value?.unresolved ? t('presets.unresolvedToken') : value?.value ?? '—';
}

function sourceText(value: SummaryValue | undefined) {
	if (!value?.source) return t('presets.unassigned');
	return value.inheritance === 'inherited'
		? `${t('presets.inherited')} ${value.source}`
		: `${t('presets.assigned')} · ${value.source}`;
}

function ColorSwatch({ value }: { value: string }) {
	return (
		<span className={css.colorSwatch} aria-hidden="true">
			<span style={{ backgroundColor: value }} />
		</span>
	);
}

export default function DesignPresetTooltip({
	children,
	open,
	onOpenChange,
	trigger = ['hover', 'focus'],
	...context
}: Parameters<typeof getDesignPresetSummary>[0] &
	Pick<React.ComponentProps<typeof Tooltip>, 'open' | 'onOpenChange' | 'trigger'> & {
		children: React.ReactNode;
	}) {
	const summary = getDesignPresetSummary(context);
	const parts = getDesignPresetNameParts(context.preset.name);
	const tablePlatforms = summary.properties[0]?.values ?? [];
	const { defaultPlatform } = context.mediaQuery ?? DESIGN_PRESET_PREVIEW_MEDIA_QUERY;
	const content = (
		<div
			className={css.tooltip}
			onKeyDown={event => {
				if (event.key === 'Escape') onOpenChange?.(false);
			}}
		>
			<div className={css.tooltipHeader}>
				{parts.length > 1 && <span className={css.tooltipPath}>{parts.slice(0, -1).join(' / ')}</span>}
				<strong className={css.tooltipName}>{parts.at(-1) ?? context.preset.name}</strong>
				{context.preset.description && <p className={css.description}>{context.preset.description}</p>}
			</div>
			<div className={css.tooltipBody}>
				{context.platform && (
					<span className={css.currentPlatform}>
						{t('presets.currentPlatform')}: {summary.currentPlatformLabel} · {summary.currentPlatformWidth}
						px
					</span>
				)}
				{summary.properties.length > 0 && (
					<div className={css.tableScroll}>
						<table className={css.platformTable}>
							<thead>
								<tr>
									<th scope="col">{t('presets.propertiesLabel')}</th>
									{tablePlatforms.map(platform => (
										<th
											key={platform.platform}
											scope="col"
											aria-label={platform.label}
											data-current={
												!!context.platform && platform.platform === summary.currentPlatform
											}
										>
											<MediaQueryTooltip
												mq={platform.platform}
												isDefault={platform.platform === defaultPlatform}
												placement="top"
											>
												<MediaQueryIcon
													platform={platform.platform}
													defaultPlatform={defaultPlatform}
													width={22}
													height={22}
												/>
											</MediaQueryTooltip>
										</th>
									))}
								</tr>
							</thead>
							<tbody>
								{summary.properties.map(item => {
									const propertyLabel = t(`presets.properties.${item.property}`);
									const isColor = ['color', 'backgroundColor', 'borderColor'].includes(item.property);
									return (
										<tr key={item.property}>
											<th scope="row">{propertyLabel}</th>
											{item.values.map(value => {
												const showSwatch =
													isColor && value.value !== undefined && !value.unresolved;
												return (
													<Tooltip
														key={value.platform}
														stylePreset="dashboard"
														placement="top"
														trigger={['hover', 'focus']}
														content={
															<div className={css.valueTooltip}>
																<strong>{propertyLabel}</strong>
																<span className={css.valueTooltipValue}>
																	{showSwatch && (
																		<ColorSwatch value={String(value.value)} />
																	)}
																	<span>{valueText(value)}</span>
																</span>
																<span
																	className={css.valueSource}
																	data-inheritance={value.inheritance}
																	data-unresolved={value.unresolved}
																>
																	{sourceText(value)}
																</span>
															</div>
														}
													>
														<td
															data-current={
																!!context.platform &&
																value.platform === summary.currentPlatform
															}
															data-inheritance={value.inheritance}
															data-unresolved={value.unresolved}
															data-color={showSwatch}
															tabIndex={showSwatch ? 0 : undefined}
															aria-label={`${propertyLabel}: ${valueText(value)}. ${sourceText(value)}`}
														>
															{showSwatch ? (
																<ColorSwatch value={String(value.value)} />
															) : (
																valueText(value)
															)}
														</td>
													</Tooltip>
												);
											})}
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}
				{summary.properties.length > 0 ? (
					<span className={css.legend}>
						<span data-inheritance="assigned">{t('presets.assigned')}</span>
						<span data-inheritance="inherited">{t('presets.inheritedValue')}</span>
						{summary.properties.some(item => item.values.some(value => value.inheritance === 'empty')) && (
							<span>{t('presets.unassigned')}</span>
						)}
					</span>
				) : (
					<span>{t('presets.emptyPreset')}</span>
				)}
			</div>
		</div>
	);
	return (
		<Tooltip
			content={content}
			stylePreset="dashboard"
			placement="left"
			trigger={trigger}
			// Allow the pointer to cross the arrow gap into the scrollable table.
			mouseLeaveDelay={0.1}
			open={open}
			onOpenChange={onOpenChange}
			stylesContainer={{ width: 'min(560px, calc(100vw - 32px))' }}
		>
			{children}
		</Tooltip>
	);
}
